import React from 'react';
import { AlertTriangle, CheckCircle2, Clock, History, Printer, Search, UserCog, type LucideIcon } from 'lucide-react';
import {
  ROTULO_MOTIVO_ROBO,
  classificarStatusRateio,
  motivoDoRobo,
  type MotivoRobo,
  type StatusRateio,
} from '../lib/janelaFaturamentoLista';
import type { PrevisaoFatura } from '../../../src/lib/previsaoFaturaEquatorial';

// Chips da Janela de Faturamento/Cobrança, compartilhados entre os cards
// (Semana/Dia), a lista e o relatório do robô — uma cor e um rótulo por status.

const BASE = 'inline-flex items-center gap-1 max-w-full px-1.5 py-0.5 rounded-full text-[10px] font-bold';

const COR_STATUS_RATEIO: Record<StatusRateio, string> = {
  ativa: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300',
  ativa_sem_rateio: 'bg-slate-100 text-slate-600 dark:bg-slate-700/60 dark:text-slate-300',
  primeiro_ciclo: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300',
  segundo_ciclo: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300',
  cancelada: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300',
  reativacao: 'bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300',
  pausada: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300',
  troca_endereco: 'bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300',
  outro: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

export const ChipStatusRateio: React.FC<{ status?: string | null; rateio?: string | null }> = ({ status, rateio }) => {
  const s = classificarStatusRateio(status);
  if (!s) return null;
  return (
    <span
      className={`${BASE} ${COR_STATUS_RATEIO[s.codigo]}`}
      title={`Status da UC no rateio${rateio ? ` ${rateio}` : ''}: ${status}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70 shrink-0" />
      <span className="truncate min-w-0">{s.rotulo}</span>
    </span>
  );
};

const ESTILO_MOTIVO: Record<MotivoRobo, { cor: string; Icone: LucideIcon }> = {
  nao_disponivel: { cor: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300', Icone: Search },
  bordero_pendente: { cor: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300', Icone: Clock },
  dados_incorretos: { cor: 'bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300', Icone: AlertTriangle },
  site_instavel: { cor: 'bg-slate-100 text-slate-600 dark:bg-slate-700/60 dark:text-slate-300', Icone: Clock },
  ja_cadastrada: { cor: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300', Icone: CheckCircle2 },
  outro: { cor: 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-300', Icone: AlertTriangle },
};

/** Resultado da última busca do robô. `texto` substitui o rótulo padrão do motivo. */
export const ChipMotivoRobo: React.FC<{ motivo: MotivoRobo; detalhe?: string | null; texto?: string }> = ({
  motivo,
  detalhe,
  texto,
}) => {
  const { cor, Icone } = ESTILO_MOTIVO[motivo];
  return (
    <span className={`${BASE} ${cor} cursor-help`} title={`Última busca do robô: ${detalhe || 'sem detalhes'}`}>
      <Icone size={10} className="shrink-0" />
      <span className="truncate min-w-0">{texto ?? ROTULO_MOTIVO_ROBO[motivo]}</span>
    </span>
  );
};

/** 'YYYY-MM-DD' → 'DD/MM/YYYY' sem passar por Date (o fuso tiraria um dia). */
const dataBr = (iso?: string | null) => (iso ? iso.slice(0, 10).split('-').reverse().join('/') : '');

/**
 * Sem fatura depois do prazo de chegada (src/lib/previsaoFaturaEquatorial.ts). Normal: 9º dia
 * útil da leitura — em mês normal só 1% a 7% passam disso. Impressa na hora: a fatura existe
 * desde a leitura, então o atraso é da busca, não da Equatorial.
 */
export const ChipFaturaAtrasada: React.FC<{ previsao?: PrevisaoFatura | null }> = ({ previsao }) => {
  const naHora = previsao?.perfil === 'impressa_na_hora';
  const titulo = naHora
    ? `Fatura impressa na hora pelo leiturista: existe desde a leitura (${dataBr(previsao?.leitura)}) e ainda não foi baixada.`
    : `Sem fatura depois do 9º dia útil da leitura.${previsao?.apresentacao ? ` A apresentação era prevista para ${dataBr(previsao.apresentacao)}.` : ''}`;
  return (
    <span className={`${BASE} bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 cursor-help`} title={titulo}>
      <Clock size={10} className="shrink-0" />
      <span className="truncate min-w-0">{naHora ? 'Fatura não baixada' : 'Atrasada na Equatorial'}</span>
    </span>
  );
};

/**
 * O limite de emissão (vencimento da Equatorial − 7 dias) cai até 2 dias depois de a fatura
 * chegar (src/lib/previsaoFaturaEquatorial.ts): emitir assim que ela chegar.
 */
export const ChipPrazoCurto: React.FC<{ chegada?: string | null; limite?: string | null }> = ({ chegada, limite }) => (
  <span
    className={`${BASE} bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 cursor-help`}
    title={`A fatura deve chegar em ${dataBr(chegada) || '—'} e o limite para emitir é ${dataBr(limite) || '—'}. Emita assim que ela chegar.`}
  >
    <AlertTriangle size={10} className="shrink-0" />
    <span className="truncate min-w-0">Prazo curto</span>
  </span>
);

/** UC com fatura impressa na hora nos últimos meses: a janela começa no dia seguinte à leitura. */
export const ChipImpressaNaHora: React.FC = () => (
  <span
    className={`${BASE} bg-sky-50 text-sky-700 dark:bg-sky-900/20 dark:text-sky-300 cursor-help`}
    title="O leiturista imprime a fatura na hora: ela costuma existir no próprio dia da leitura. Por isso esta UC entra na janela no dia útil seguinte à leitura."
  >
    <Printer size={10} className="shrink-0" />
    <span className="truncate min-w-0">Impressa na hora</span>
  </span>
);

/** Cabeçalho de uma entrada do relatório de tentativas (historico_execucoes). */
export const ChipEntradaRobo: React.FC<{ status?: string; mensagem?: string | null }> = ({ status, mensagem }) => {
  if (status === 'concluido') {
    return (
      <span className={`${BASE} bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300`}>
        <CheckCircle2 size={10} className="shrink-0" /> Fatura baixada
      </span>
    );
  }
  if (status === 'reiniciado') {
    return (
      <span className={`${BASE} bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300`}>
        <History size={10} className="shrink-0" /> Reiniciado manualmente
      </span>
    );
  }
  if (status === 'dados_atualizados') {
    return (
      <span className={`${BASE} bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300`}>
        <UserCog size={10} className="shrink-0" /> Cadastro corrigido
      </span>
    );
  }
  return <ChipMotivoRobo motivo={motivoDoRobo(mensagem)} detalhe={mensagem} />;
};
